import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { organizations, type BrandingPatchBody } from "@/api/organizations";
import { useBranding, useRefreshBranding } from "@/branding/BrandingLoader";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  FormError,
  Input,
  Label,
  Textarea,
} from "@/components/ui";
import { useToast } from "@/components/Toast";

const FIELDS: { key: keyof BrandingPatchBody; label: string; placeholder?: string }[] = [
  { key: "primary_color", label: "Primary color (hex)", placeholder: "#0E66FF" },
  { key: "accent_color", label: "Accent color (hex)", placeholder: "#FFC400" },
  { key: "logo_url", label: "Logo URL", placeholder: "https://cdn.example.com/logo.svg" },
  { key: "favicon_url", label: "Favicon URL" },
  { key: "company_name", label: "Company name" },
];

export function AdminBrandingPage() {
  const branding = useBranding();
  const refresh = useRefreshBranding();
  const toast = useToast();

  const [form, setForm] = useState<BrandingPatchBody>({});
  const [jsonText, setJsonText] = useState<string>("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [tab, setTab] = useState<"fields" | "json">("fields");
  const [error, setError] = useState<string | null>(null);

  // Pull current values into the form on first load and after each save.
  useEffect(() => {
    const current = (branding?.branding ?? {}) as BrandingPatchBody;
    setForm(current);
    setJsonText(JSON.stringify(branding?.branding ?? {}, null, 2));
  }, [branding]);

  const save = useMutation({
    mutationFn: (body: BrandingPatchBody) => organizations.patchBranding(body),
    onSuccess: async () => {
      toast.push("Branding saved", "success");
      await refresh();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  function update<K extends keyof BrandingPatchBody>(k: K, v: string) {
    setForm((cur) => ({ ...cur, [k]: v === "" ? null : v }));
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Branding</h1>
        <div className="flex rounded-md border border-slate-200 dark:border-slate-800 p-0.5">
          <TabBtn active={tab === "fields"} onClick={() => setTab("fields")}>Fields</TabBtn>
          <TabBtn active={tab === "json"} onClick={() => setTab("json")}>JSON</TabBtn>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div>
            <h2 className="text-sm font-semibold">Current</h2>
            <p className="mt-1 text-xs text-slate-500">
              Resolved by <code>GET /api/v1/branding</code>. Match: <code>{branding?.matched_via ?? "—"}</code>.
            </p>
          </div>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Org</div>
              <p className="mt-1">{branding?.name ?? "—"}</p>
              <p className="mt-1 text-xs text-slate-500">slug: {branding?.slug ?? "—"}</p>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Preview</div>
              <div className="mt-2 flex items-center gap-2">
                {branding?.branding.logo_url && (
                  <img src={branding.branding.logo_url as string} alt="" className="h-10" />
                )}
                <span
                  className="inline-block h-6 w-6 rounded-md border border-slate-300"
                  style={{ background: (branding?.branding.primary_color as string | undefined) ?? "#0E66FF" }}
                />
                <span className="text-xs">
                  {(branding?.branding.primary_color as string | undefined) ?? "#0E66FF"}
                </span>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {tab === "fields" ? (
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold">Edit branding</h2>
          </CardHeader>
          <CardBody className="space-y-3">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <Input
                  value={(form[f.key] as string | null | undefined) ?? ""}
                  placeholder={f.placeholder}
                  onChange={(e) => update(f.key, e.target.value)}
                />
              </div>
            ))}
            <FormError>{error}</FormError>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="ghost"
                onClick={() => setForm((branding?.branding ?? {}) as BrandingPatchBody)}
              >
                Reset
              </Button>
              <Button
                loading={save.isPending}
                onClick={() => {
                  setError(null);
                  save.mutate(form);
                }}
              >
                Save
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold">JSON editor</h2>
          </CardHeader>
          <CardBody>
            <Textarea
              rows={16}
              value={jsonText}
              onChange={(e) => {
                setJsonText(e.target.value);
                try {
                  JSON.parse(e.target.value);
                  setJsonError(null);
                } catch {
                  setJsonError("Invalid JSON");
                }
              }}
            />
            <FormError>{jsonError ?? error}</FormError>
            <p className="mt-2 text-xs text-slate-500">
              Recognized fields: <code>primary_color</code>, <code>accent_color</code>,{" "}
              <code>logo_url</code>, <code>favicon_url</code>, <code>company_name</code>,{" "}
              <code>css_overrides</code>. Send a key as <code>null</code> to remove it.
            </p>
            <div className="flex justify-end gap-2 pt-3">
              <Button
                disabled={!!jsonError}
                loading={save.isPending}
                onClick={() => {
                  try {
                    setError(null);
                    save.mutate(JSON.parse(jsonText) as BrandingPatchBody);
                  } catch (err) {
                    setError(String(err));
                  }
                }}
              >
                Save JSON
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-sm px-3 py-1 text-xs font-medium transition-colors ${
        active
          ? "bg-brand text-white"
          : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
      }`}
    >
      {children}
    </button>
  );
}
